import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { updateProfileSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import { Client } from '../../../types/users';
import { showPicture } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCountrySelect from '../Inputs/AcademiaCountrySelect';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const UpdateClientProfileModal = ({
  handleModalClose,
  client,
}: {
  handleModalClose: () => void;
  client: Client;
}) => {
  // destructuring the client object here
  const {
    id,
    username,
    first_name,
    middle_name,
    last_name,
    email,
    country_name,
    phone,
    address,
    profile_picture,
    province_id,
    province_name,
    city_id,
    city_name,
    postal_code,
    iso_code,
  } = client || {};

  // integration of react hooks here
  const [countryCode, setCountryCode] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  const [hasCountryReset, setHasCountryReset] = useState<boolean>(false);
  const [hasProvinceReset, setHasProvinceReset] = useState<boolean>(false);
  const [hasCityReset, setHasCityReset] = useState<boolean>(false);

  const [selectedProvince, setSelectedProvince] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedCity, setSelectedCity] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  // integration of custom API hooks here
  const { updateClientHandler } = useClientActions('');

  const { getCountryWiseAddedProvincesHandler } = useProvincesActions(
    '',
    undefined,
    selectedCountry.code
  );
  const { getProvinceWiseCitiesHandler } = useCitiesActions(
    '',
    selectedProvince.id !== -1 ? selectedProvince.id : undefined
  );
  const { getPostalCodesWithCityIdHandler } = usePostalCodesActions(
    '',
    selectedCity.id !== -1 ? selectedCity.id : undefined
  );

  // integration tanstack query hooks here
  const queryClient = useQueryClient();

  // checking if country has been reselected or not here
  useEffect(() => {
    if (selectedCountry.id !== -1 && country_name !== selectedCountry.name) {
      setHasCountryReset(true);
      setSelectedProvince(SELECT_FIELD_DEFAULT_VALUE);
      setSelectedCity(SELECT_FIELD_DEFAULT_VALUE);
    }
  }, [selectedCountry, country_name]);

  // setting default values of province & city
  useEffect(() => {
    setSelectedProvince({ id: province_id, name: province_name });
    setSelectedCity({ id: city_id, name: city_name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provinceOptions = useMemo(() => {
    if (!getCountryWiseAddedProvincesHandler.data?.is_success) return [];

    return getCountryWiseAddedProvincesHandler.data.data.map((province) => ({
      id: province.id,
      name: province.province_name,
    }));
  }, [getCountryWiseAddedProvincesHandler.data]);

  const cityOptions = useMemo(() => {
    if (!getProvinceWiseCitiesHandler.data?.is_success) return [];

    return getProvinceWiseCitiesHandler.data.data.map((city) => ({
      id: city.id,
      name: city.city_name,
    }));
  }, [getProvinceWiseCitiesHandler.data]);

  const postalCodeOptions = useMemo(() => {
    if (!getPostalCodesWithCityIdHandler.data?.is_success) return [];

    return getPostalCodesWithCityIdHandler.data.data[0].postal_codes.map(
      (postalCode, index) => ({
        id: index,
        name: postalCode,
      })
    );
  }, [getPostalCodesWithCityIdHandler.data]);

  // function to update user profile declared here
  const updateUserProfile = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log('VALUES: ', values);

      const body = new FormData();

      // mandatory fields

      // personal details
      body.append('first_name', values.first_name);
      body.append('last_name', values.last_name);
      body.append('email', values.email);

      // address & phone details
      body.append('address', values.address);
      body.append('country', selectedCountry.name);
      body.append('isd_code', countryCode);
      body.append('phone', values.phone);
      body.append('city', values.city.id);
      body.append('postal_code', values.postal_code.name);
      body.append('is_profile_complete', 'true');
      body.append('updated_by', username);

      // optional fields
      values.middle_name && body.append('middle_name', values.middle_name);
      values.profile_picture &&
        body.append('profile_picture', values.profile_picture);

      await updateClientHandler
        .mutateAsync({ clientId: id as number, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Profile Updated!!');

            handleModalClose();

            queryClient.invalidateQueries({
              queryKey: ['current-client-details', id],
            });
          } else {
            toast.error(res.message || 'Updating Profile Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering update client profile modal component here
  return (
    <ParentModal
      onClick={handleModalClose}
      customWidthClass='md:w-[95%] lg:w-[900px]'
    >
      <ModalTitle titleText='update profile' />

      <Formik
        initialValues={{
          first_name: first_name || '',
          middle_name: middle_name || '',
          last_name: last_name || '',
          email: email || '',
          country: country_name || '',
          phone: phone || '',
          address: address || '',
          profile_picture: '',
          province: { id: province_id, name: province_name },
          city: { id: city_id, name: city_name },
          postal_code: { id: 0, name: postal_code },
        }}
        validationSchema={updateProfileSchema}
        onSubmit={updateUserProfile}
      >
        {(props) => (
          <Form className='space-y-4'>
            <div>
              <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                Personal Details
              </h3>

              <div className='grid md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  <AcademiaInputField
                    type='text'
                    name='first_name'
                    label='First Name'
                    placeholder='Enter first name here...'
                    isRequired
                  />

                  <AcademiaInputField
                    type='text'
                    name='middle_name'
                    label='Middle Name'
                    placeholder='Enter middle name here...'
                  />

                  <AcademiaInputField
                    type='text'
                    name='last_name'
                    label='Last Name'
                    placeholder='Enter last name here...'
                    isRequired
                  />
                </div>

                <div className='space-y-4'>
                  <AcademiaInputField
                    type='email'
                    name='email'
                    label='Email'
                    placeholder='Enter email here...'
                    isRequired
                  />

                  <AcademiaFileInputField
                    name='profile_picture'
                    label='Profile Picture'
                    btnText='Upload'
                    accept='images/*'
                    placeholder='Upload profile picture here...'
                    filePath={
                      profile_picture ? showPicture(profile_picture) : ''
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                Address & Phone Details
              </h3>

              <div className='grid md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  <AcademiaCountrySelect
                    name='country'
                    label='Select Country'
                    setCountry={setSelectedCountry}
                    defaultCountryCode={iso_code}
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='province'
                    label='Select Province'
                    options={provinceOptions}
                    defaultValue={props.values.province}
                    setValueOnChange={(province) => {
                      setSelectedProvince(province);
                      setHasProvinceReset(true);
                    }}
                    resetSelectField={hasCountryReset}
                    setResetSelectField={() => setHasCountryReset(false)}
                    showSearchInput
                    isDisabled={
                      !getCountryWiseAddedProvincesHandler.data?.is_success
                    }
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='city'
                    label='Select City'
                    options={cityOptions}
                    defaultValue={props.values.city}
                    setValueOnChange={(city) => {
                      setSelectedCity(city);
                      setHasCityReset(true);
                    }}
                    resetSelectField={hasCountryReset || hasProvinceReset}
                    setResetSelectField={() => {
                      setHasCountryReset(false);
                      setHasProvinceReset(false);
                    }}
                    showSearchInput
                    isDisabled={!getProvinceWiseCitiesHandler.data?.is_success}
                    isRequired
                  />

                  <AcademiaFormSelect
                    name='postal_code'
                    label='Select Postal Code'
                    options={postalCodeOptions}
                    defaultValue={props.values.postal_code}
                    resetSelectField={
                      hasCountryReset || hasProvinceReset || hasCityReset
                    }
                    setResetSelectField={() => {
                      setHasCountryReset(false);
                      setHasProvinceReset(false);
                      setHasCityReset(false);
                    }}
                    showSearchInput
                    isDisabled={
                      !getPostalCodesWithCityIdHandler.data?.is_success
                    }
                    isRequired
                  />
                </div>

                <div className='space-y-4'>
                  <AcademiaInputField
                    type='number'
                    name='phone'
                    label='Phone Number'
                    placeholder='Enter phone number here...'
                    setCountryCode={setCountryCode}
                    defaultSelectedCountryCode={selectedCountry.code}
                    isRequired
                  />

                  <AcademiaTextAreaField
                    name='address'
                    label='Address'
                    placeholder={`Enter user's address here...`}
                    isRequired
                  />
                </div>
              </div>
            </div>

            <div className='w-full md:w-3/5 mx-auto'>
              <AcademiaButton
                title={
                  props.values.province.id === -1
                    ? 'Please, select a province first!'
                    : props.values.city.id === -1
                    ? 'Please, select a city first!'
                    : props.values.postal_code.id === -1
                    ? 'Please, select a postal code first!'
                    : ''
                }
                type='submit'
                variant='premium'
                isLoading={props.isSubmitting}
                isDisabled={
                  props.values.province.id === -1 ||
                  props.values.city.id === -1 ||
                  props.values.postal_code.id === -1
                }
                icon={<RxUpdate className='size-6' />}
                fullWidth
              >
                update profile
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default UpdateClientProfileModal;
